import { computed } from 'vue';
import { usePatientStore } from './usePatientStore.js';
import { useDateUtils } from './useDateUtils.js';

/**
 * Results that have been recorded but not yet dosed.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 * Quick entry (/quick-entry) lets a clinician record an INR from a phone, away
 * from the workstation. Recording the value is deliberately the whole job there
 * — the dosing calendar has no usable phone layout — so every quick entry
 * leaves a result with no dose against it. That is a safe state ONLY if someone
 * sees it: a value captured and silently forgotten is a clinical problem, not a
 * UX one. This composable is the other half of the feature, surfaced in the app
 * bar on the desktop where the dose actually gets set.
 *
 * ── How "not yet dosed" is decided ─────────────────────────────────────────
 * A dosed result has `daily_doses` rows pointing at it. There is no flag on the
 * result itself to read: the `*Applied` columns on the dose-plan table belong to
 * the retired AI pipeline and are not written by the current app, so the join is
 * the only honest signal.
 *
 * Two deliberate limits follow from that:
 *
 *  - WINDOW. Only results from the last `PENDING_WINDOW_DAYS` days count. The
 *    table holds years of history, and a 2023 result with no daily_doses rows is
 *    not work in flight — it is an artefact of how that patient was managed.
 *    Without the window the badge would open on a number in the hundreds and be
 *    ignored forever after.
 *
 *  - SINGLE DOSES. A result dosed only through `single_doses` is not pending,
 *    but that table is fetched per-patient on demand (fetchSingleDosesForPatient)
 *    rather than globally, so `store.singleDoses` is usually empty. It is checked
 *    anyway — it costs nothing and is correct whenever the data happens to be
 *    loaded — but the window is what actually keeps the count honest.
 *
 * The count stays at zero until `isEnriched` is true. Doses are only loaded
 * during patient enrichment, so before that every result looks undosed, and a
 * badge that flashes "47 pending" on every cold load teaches people to ignore it.
 */

/** How far back a result can be and still count as work in flight. */
export const PENDING_WINDOW_DAYS = 14;

const dayKey = (value) => (value ? String(value).slice(0, 10) : null);

export function usePendingDoses() {
  const store = usePatientStore();
  const { toYYYYMMDD } = useDateUtils();

  /**
   * The cutoff is compared against `resultDate`, which is a local calendar day
   * as the clinician entered it. Deriving it through UTC would move the boundary
   * by a day for part of every evening, so the local-day formatter is used on
   * both sides of the comparison.
   */
  const windowStart = () => {
    const d = new Date();
    d.setDate(d.getDate() - PENDING_WINDOW_DAYS);
    return toYYYYMMDD(d);
  };

  const pendingResults = computed(() => {
    if (!store.isEnriched.value) return [];

    const cutoff = windowStart();
    const dosedResultIds = new Set([
      ...store.doses.value.map((d) => Number(d.resultId)),
      ...store.singleDoses.value.map((d) => Number(d.resultId)),
    ]);

    return store.results.value
      .filter((result) => {
        if (result?.id == null) return false;
        if (dosedResultIds.has(Number(result.id))) return false;

        const date = dayKey(result.resultDate);
        if (!date || date < cutoff) return false;

        // An archived patient's history is read-only in practice; a pending row
        // pointing at one is not something anybody is going to act on.
        const patient = store.patients.value.find((p) => Number(p.id) === Number(result.patientId));
        return Boolean(patient) && patient.isActive !== false;
      })
      .map((result) => {
        const patient = store.patients.value.find((p) => Number(p.id) === Number(result.patientId));
        return {
          id: result.id,
          patientId: result.patientId,
          patientName: patient ? `${patient.firstName ?? ''} ${patient.name ?? ''}`.trim() : '',
          resultValue: result.resultValue,
          resultDate: dayKey(result.resultDate),
        };
      })
      .sort((a, b) => b.resultDate.localeCompare(a.resultDate));
  });

  const pendingCount = computed(() => pendingResults.value.length);

  return {
    pendingResults,
    pendingCount,
  };
}
